import { InMemoryCache, makeVar } from '@apollo/client';
import {
  Listing,
  OrderByField,
  OrderByInput,
  OrderBySortDirection,
  PricePredictionResponse,
  VendorType,
  Website,
  Query,
  Maybe,
  QueryGetPricePredictionArgs,
  VehicleType,
  Country,
} from 'generated/graphql';

export const powerSupplies = makeVar<string[]>([]);
export const country = makeVar<Country>(Country.Italy);
export const gearTypes = makeVar<string[]>([]);
export const modelNames = makeVar<string[]>([]);
export const modelYear = makeVar<number[]>([]);
export const optionals = makeVar<string[]>([]);
export const brandNames = makeVar<string[]>([]);
export const vendorNames = makeVar<string[]>([]);
export const tractionTypes = makeVar<string[]>([]);
export const vendorType = makeVar<string>(VendorType.Dealer);
export const vehicleTypes = makeVar<VehicleType[] | undefined>(undefined);
export const websites = makeVar<Website[]>([]);
export const regions = makeVar<string[]>([]);
export const defaultRegions = makeVar<string[]>([]);
export const defaultVendorName = makeVar<string | undefined>(undefined);
export const provinces = makeVar<string[]>([]);
export const priceLabels = makeVar<string[]>([]);
export const isListingActive = makeVar<string>('Active');
export const isPriceTiedToLoan = makeVar<boolean | undefined | null>(undefined);
export const isVehicleCoveredByWarranty = makeVar<boolean | undefined | null>(undefined);
export const isPictureAvailable = makeVar<boolean | undefined | null>(undefined);
export const isVATDeductible = makeVar<boolean | undefined | null>(undefined);
export const interiorColors = makeVar<string[]>([]);
export const coatingColors = makeVar<string[]>([]);
export const vehicleRegistrationDateISO8601 = makeVar<{ min?: string; max?: string }>({});
export const powerInHP = makeVar<{ min?: string; max?: string }>({});
export const vehicleKM = makeVar<{ min?: string; max?: string }>({});
export const priceInEUR = makeVar<{ min?: string; max?: string }>({});
export const engineDisplacementInCM3 = makeVar<{ min?: string; max?: string }>({});
export const eurotaxCodes = makeVar<string[]>([]);
export const modelVersions = makeVar<{ value?: string; label?: string }[]>([]);
export const predictedPricesForItaly = makeVar<Partial<Query['getPricePredictionForItaly']>>(undefined);
export const code = makeVar<{ kind?: 'eurotax' | 'plate' | 'constructor_code' | string; value?: string }>({});
export const comparison = makeVar<Partial<Listing> & { vehicleRegistrationPlate?: string; eurotaxCode?: string }>({});
export const prediction = makeVar<Partial<PricePredictionResponse>>({});

export const predictedPrices = makeVar<
  ReadonlyArray<
    PricePredictionResponse & {
      id: string;
      modelVersions?: Maybe<ReadonlyArray<{ value: string; label: string }>>;
      eurotaxCodes?: QueryGetPricePredictionArgs['eurotaxCodes'];
      provinceList?: QueryGetPricePredictionArgs['provinceList'];
      country?: string;
      regionList?: QueryGetPricePredictionArgs['regionList'];
      optionalList?: QueryGetPricePredictionArgs['vehicleOptionals']; // FIXME
      vendorType: string;
    }
  >
>([]);

export const orderBy = makeVar<Partial<OrderByInput>>({
  field: OrderByField.PublishDateIso8601,
  sortDirection: OrderBySortDirection.Desc,
});

export const sharedState = makeVar<
  Partial<{
    selectedModel: ReadonlyArray<{ value?: string; label?: string }>;
    selectedYear: string;
    predictedPrice: Query['getPricePrediction'];
  }>
>({});

export const cache = new InMemoryCache({
  addTypename: false,
  typePolicies: {
    Query: {
      fields: {
        sharedState: {
          read(): unknown {
            return sharedState();
          },
        },
        comparison: {
          read(): unknown {
            return comparison();
          },
        },
        prediction: {
          read(): unknown {
            return prediction();
          },
        },
        powerSupplies: {
          read(): string[] | undefined {
            return powerSupplies();
          },
        },
        gearTypes: {
          read(): string[] | undefined {
            return gearTypes();
          },
        },
        modelNames: {
          read(): string[] | undefined {
            return modelNames();
          },
        },
        modelYear: {
          read(): number[] | undefined {
            return modelYear();
          },
        },
        brandNames: {
          read(): string[] | undefined {
            return brandNames();
          },
        },
        tractionTypes: {
          read(): string[] | undefined {
            return tractionTypes();
          },
        },
        vendorType: {
          read(): string | undefined {
            return vendorType();
          },
        },
        vehicleTypes: {
          read(): VehicleType[] | undefined {
            return vehicleTypes();
          },
        },
        regions: {
          read(): string[] | undefined {
            return regions();
          },
        },
        defaultRegions: {
          read(): string[] | undefined {
            return defaultRegions();
          },
        },
        interiorColors: {
          read(): string[] | undefined {
            return interiorColors();
          },
        },
        coatingColors: {
          read(): string[] | undefined {
            return coatingColors();
          },
        },
        provinces: {
          read(): string[] | undefined {
            return provinces();
          },
        },
        isListingActive: {
          read(): string | undefined {
            return isListingActive();
          },
        },
        isPriceTiedToLoan: {
          read(): boolean | undefined | null {
            return isPriceTiedToLoan();
          },
        },
        isVehicleCoveredByWarranty: {
          read(): boolean | undefined | null {
            return isVehicleCoveredByWarranty();
          },
        },
        isPictureAvailable: {
          read(): boolean | undefined | null {
            return isPictureAvailable();
          },
        },
        isVATDeductible: {
          read(): boolean | undefined | null {
            return isVATDeductible();
          },
        },
        priceLabels: {
          read(): string[] | undefined {
            return priceLabels();
          },
        },
        vehicleKM: {
          read(): { min?: string; max?: string } {
            return vehicleKM();
          },
        },
        powerInHP: {
          read(): { min?: string; max?: string } {
            return powerInHP();
          },
        },
        priceInEUR: {
          read(): { min?: string; max?: string } {
            return priceInEUR();
          },
        },
        vehicleRegistrationDateISO8601: {
          read(): { min?: string; max?: string } {
            return vehicleRegistrationDateISO8601();
          },
        },
        engineDisplacementInCM3: {
          read(): { min?: string; max?: string } {
            return engineDisplacementInCM3();
          },
        },
        orderBy: {
          read(): { field?: string; sortDirection?: string } {
            return orderBy();
          },
        },
        eurotaxCodes: {
          read(): string[] | undefined {
            return eurotaxCodes();
          },
        },
        modelVersions: {
          read(): { label?: string; value?: string }[] | undefined {
            return modelVersions();
          },
        },
        optionals: {
          read(): string[] | undefined {
            return optionals();
          },
        },
        vendorNames: {
          read(): string[] | undefined {
            return vendorNames();
          },
        },
        websites: {
          read(): Website[] | undefined {
            return websites();
          },
        },
        predictedPrices: {
          read(): ReadonlyArray<Partial<PricePredictionResponse & { id: string }>> | undefined {
            return predictedPrices();
          },
        },
        predictedPricesForItaly: {
          read(): Partial<Query['getPricePredictionForItaly']> {
            return predictedPricesForItaly();
          },
        },
        code: {
          read(): { value?: string; kind?: string } {
            return code();
          },
        },
        vendorName: {
          read(): string | undefined {
            return defaultVendorName();
          },
        },
        country: {
          read(): 'ITALY' | 'GERMANY' {
            return country();
          },
        },
      },
    },
  },
});
